/* Banking Portal · Waves 21/23/24/27/36/40/42/43/47/50 — Full closeout (Feb 2026).
   Ten examiner-grade pages consolidated into one file. */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, TrendingUp, AlertTriangle, CheckCircle2, XCircle,
  Users, FileCheck2, Scale, ShieldCheck, RefreshCw,
  Clock, Hash, Globe,
  Banknote, Lock, Plug, Activity, FileSearch, BookOpen,
  Mail, KeyRound, AlertOctagon, Sigma,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";

function fmt(n) { return n == null ? "—" : new Intl.NumberFormat("en-US").format(n); }
function money(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}
function ts(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }
function dateOnly(iso) { return iso ? new Date(iso).toLocaleDateString() : "—"; }

const TONE_CLS = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber:   "bg-amber-50 text-amber-700 border-amber-200",
  rose:    "bg-rose-50 text-rose-700 border-rose-200",
  sky:     "bg-sky-50 text-sky-700 border-sky-200",
  slate:   "bg-slate-50 text-slate-700 border-slate-200",
  violet:  "bg-violet-50 text-violet-700 border-violet-200",
};

function Kpi({ label, value, sub, tone = "slate", icon: Icon, testid }) {
  const cls = {
    slate:   "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
    violet:  "bg-violet-50 border-violet-200",
  }[tone] || "bg-slate-50 border-slate-200";
  return (
    <div data-testid={testid} className={`rounded-xl border ${cls} px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[10.5px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-27 · PREDICTIVE ANOMALY FORECASTING
// ═════════════════════════════════════════════════════════════════════════
export function BankingPredictive() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/partner/banking/predictive/forecast?window_days=30&limit=50")
      .then(r => setData(r.data))
      .catch(() => toast.error("Predictive forecast failed"));
  }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-predictive">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-violet-500" /> Predictive Forecasting
          </h1>
          <Badge className="bg-violet-50 text-violet-700 border-violet-200">Wave 27</Badge>
          <Badge variant="outline">Acted in advance</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Evaluated" value={fmt(data.evaluated)} sub="Active customers" icon={Users} testid="kpi-pred-eval" />
        <Kpi label="Flagged" value={fmt(data.flagged)} sub="Above threshold" tone="amber" icon={AlertTriangle} testid="kpi-pred-flagged" />
        <Kpi label="7-day watch" value={fmt(data.by_window["7d"])} sub="Highest risk" tone="rose" icon={Clock} testid="kpi-pred-7d" />
        <Kpi label="14-day watch" value={fmt(data.by_window["14d"])} sub="Elevated" tone="amber" icon={Clock} testid="kpi-pred-14d" />
        <Kpi label="30-day watch" value={fmt(data.by_window["30d"])} sub="Moderate" tone="sky" icon={Clock} testid="kpi-pred-30d" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50"><tr className="text-left text-slate-500">
            <th className="px-3 py-2">Customer (masked)</th>
            <th className="px-3 py-2 text-right">Composite</th>
            <th className="px-3 py-2">Window</th>
            <th className="px-3 py-2 text-right">Velocity</th>
            <th className="px-3 py-2 text-right">Drawdown</th>
            <th className="px-3 py-2 text-right">Rule prox.</th>
            <th className="px-3 py-2 text-right">Open AML</th>
          </tr></thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`pred-row-${i}`}>
                <td className="px-3 py-2">
                  <div>{r.user_name_masked}</div>
                  <div className="text-[10px] text-slate-400">{r.user_email_masked} · {r.user_company} · {r.user_role}</div>
                </td>
                <td className="px-3 py-2 text-right"><Badge className={TONE_CLS[r.tone]}>{r.composite_score}</Badge></td>
                <td className="px-3 py-2"><Badge className={TONE_CLS[r.tone]}>{r.predicted_window}</Badge></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.signals.velocity_score} <span className="text-[9px] text-slate-400">({r.signals.velocity_delta_pct}%)</span></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.signals.drawdown_score}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.signals.rule_score}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.signals.aml_open_high_crit}</td>
              </tr>
            ))}
            {!data.rows.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-emerald-600">✓ No accounts above threshold — clean book.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700">
        <div className="font-semibold mb-1 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Methodology</div>
        {data.methodology}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-21 · CUSTOMER RISK RATING + EDD
// ═════════════════════════════════════════════════════════════════════════
export function BankingCrr() {
  const [data, setData] = useState(null);
  const [tierFilter, setTierFilter] = useState("all");
  const [needsReview, setNeedsReview] = useState("all");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [newTier, setNewTier] = useState("");
  const [note, setNote] = useState("");
  const [eddCompleted, setEddCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (tierFilter !== "all") params.set("tier_filter", tierFilter);
    if (needsReview !== "all") params.set("needs_review", needsReview === "yes" ? "true" : "false");
    api.get(`/partner/banking/crr/roster?${params}`)
      .then(r => setData(r.data))
      .catch(() => toast.error("CRR roster failed"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tierFilter, needsReview]);

  const submitReview = async () => {
    if (note.length < 8) { toast.error("Note ≥8 chars"); return; }
    setSubmitting(true);
    try {
      await api.post(`/partner/banking/crr/${target.user_id}/review`, {
        new_tier: newTier || undefined, note, edd_completed: eddCompleted,
      });
      toast.success("Review filed · audit chained");
      setReviewOpen(false); setNote(""); setNewTier(""); setEddCompleted(false); setTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Review failed"); }
    finally { setSubmitting(false); }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-crr">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Users className="w-6 h-6 text-amber-500" /> Customer Risk Rating + EDD</h1>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">Wave 21</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total customers" value={fmt(data.total)} sub="In roster" icon={Users} testid="kpi-crr-total" />
        <Kpi label="Needs review" value={fmt(data.needs_review_count)} sub="Annual or tier mismatch" tone="amber" icon={Clock} testid="kpi-crr-review" />
        <Kpi label="EDD overdue" value={fmt(data.edd_overdue_count)} sub="T2/T3 missing" tone="rose" icon={FileSearch} testid="kpi-crr-edd" />
        <Kpi label="T3 critical" value={fmt(data.by_recommended_tier.T3_critical || 0)} sub="Highest tier" tone="rose" icon={AlertTriangle} testid="kpi-crr-t3" />
      </div>
      <div className="flex gap-3 items-center flex-wrap">
        <Select value={tierFilter} onValueChange={setTierFilter}>
          <SelectTrigger className="w-48" data-testid="select-crr-tier"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            <SelectItem value="T0_normal">T0 normal</SelectItem>
            <SelectItem value="T1_watch">T1 watch</SelectItem>
            <SelectItem value="T2_elevated">T2 elevated</SelectItem>
            <SelectItem value="T3_critical">T3 critical</SelectItem>
          </SelectContent>
        </Select>
        <Select value={needsReview} onValueChange={setNeedsReview}>
          <SelectTrigger className="w-48" data-testid="select-crr-needs-review"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Needs review</SelectItem>
            <SelectItem value="no">Ontrack</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50"><tr className="text-left text-slate-500">
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Current</th>
            <th className="px-3 py-2">Recommended</th>
            <th className="px-3 py-2">Volume 30d</th>
            <th className="px-3 py-2">AML</th>
            <th className="px-3 py-2">Last review</th>
            <th className="px-3 py-2">EDD</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr></thead>
          <tbody>
            {data.rows.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`crr-row-${i}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.user_name_masked}</div>
                  <div className="text-[10px] text-slate-400">{r.user_email_masked} · {r.user_company}</div>
                </td>
                <td className="px-3 py-2"><Badge variant="outline">{r.current_tier}</Badge></td>
                <td className="px-3 py-2">
                  <Badge className={TONE_CLS[r.tone]}>{r.recommended_tier}</Badge>
                  {!r.tier_match && <div className="text-[9px] text-rose-600 mt-0.5">↑ tier mismatch</div>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.volume_30d_usd)}</td>
                <td className="px-3 py-2 tabular-nums">{r.aml_open}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-600">{r.last_review_at ? `${r.days_since_review}d ago` : <span className="italic text-rose-500">never</span>}</td>
                <td className="px-3 py-2">{r.edd_required ? (r.edd_completed ? "✓" : <span className="text-rose-500">✗</span>) : "—"}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => { setTarget(r); setReviewOpen(true); }} data-testid={`btn-review-${i}`}>Review</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent data-testid="dialog-crr-review">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-emerald-500" /> Customer review</DialogTitle>
            <DialogDescription>
              <strong>{target?.user_name_masked}</strong> · {target?.user_company} · current {target?.current_tier} · recommended {target?.recommended_tier}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11.5px]">New tier (optional · accept recommendation if blank)</Label>
              <Select value={newTier} onValueChange={setNewTier}>
                <SelectTrigger data-testid="select-new-tier"><SelectValue placeholder="No change" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="T0_normal">T0 normal</SelectItem>
                  <SelectItem value="T1_watch">T1 watch</SelectItem>
                  <SelectItem value="T2_elevated">T2 elevated</SelectItem>
                  <SelectItem value="T3_critical">T3 critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11.5px]"><input type="checkbox" checked={eddCompleted} onChange={e => setEddCompleted(e.target.checked)} className="mr-1" data-testid="checkbox-edd" /> EDD checklist completed (T2/T3 only)</Label>
            </div>
            <div>
              <Label className="text-[11.5px]">Review note (≥8 chars · audit-logged)</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Annual review · no material changes · source of funds re-verified" data-testid="input-crr-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Cancel</Button>
            <Button onClick={submitReview} disabled={submitting || note.length < 8} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirm-review">
              {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-2" />}File review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-24 · GLBA + RESILIENCE
// ═════════════════════════════════════════════════════════════════════════
export function BankingGlba() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/resilience/glba").then(r => setData(r.data)).catch(() => toast.error("GLBA load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const glbaEntries = Object.entries(data.glba);
  return (
    <div className="space-y-5" data-testid="banking-glba">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-emerald-500" /> Operational Resilience + GLBA</h1>
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Wave 24</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="GLBA controls" value={`${data.summary.glba_controls_compliant}/${data.summary.glba_controls_total}`} sub="Compliant" tone="emerald" icon={ShieldCheck} testid="kpi-glba" />
        <Kpi label="DR drills logged" value={fmt(data.summary.drills_logged)} sub="Tabletop history" tone={data.summary.drills_logged ? "emerald" : "amber"} icon={Activity} testid="kpi-drills" />
        <Kpi label="RTO target" value={`${data.summary.rto_target_hours}h`} sub="Recovery time obj" icon={Clock} testid="kpi-rto" />
        <Kpi label="RPO target" value={`${data.summary.rpo_target_hours}h`} sub="Recovery point obj" icon={Clock} testid="kpi-rpo" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-emerald-500" /> GLBA Safeguards Rule controls</div>
        <div className="space-y-2">
          {glbaEntries.map(([key, v]) => (
            <div key={key} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-[11.5px]" data-testid={`glba-${key}`}>
              <div>
                <div className="font-medium">{key.replaceAll("_", " ")}</div>
                <div className="text-[10px] text-slate-500">{v.present === false ? "missing" : Object.entries(v).filter(([k]) => !["tone", "present"].includes(k)).map(([k,val]) => `${k}: ${val}`).join(" · ")}</div>
              </div>
              <Badge className={TONE_CLS[v.tone]}>{v.tone === "emerald" ? "✓ in place" : v.tone === "amber" ? "partial" : "gap"}</Badge>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-2 flex items-center gap-2"><Activity className="w-4 h-4 text-sky-500" /> DR/BCP posture</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11.5px]">
          <div><div className="text-slate-500 text-[10px]">RTO target</div><div className="font-semibold">{data.bcp_posture.rto_target_hours}h</div></div>
          <div><div className="text-slate-500 text-[10px]">RPO target</div><div className="font-semibold">{data.bcp_posture.rpo_target_hours}h</div></div>
          <div><div className="text-slate-500 text-[10px]">Last drill RTO</div><div className="font-semibold">{data.bcp_posture.last_drill_rto_actual_hours ?? "—"}</div></div>
          <div><div className="text-slate-500 text-[10px]">Last drill RPO</div><div className="font-semibold">{data.bcp_posture.last_drill_rpo_actual_hours ?? "—"}</div></div>
        </div>
      </div>
      {data.gaps.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-4">
          <div className="font-semibold text-rose-900 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Honest gaps</div>
          {data.gaps.map((g, i) => (
            <div key={i} className="text-[11.5px] mb-1.5" data-testid={`glba-gap-${i}`}>
              <Badge className={TONE_CLS[g.severity === "high" ? "rose" : "amber"]}>{g.severity}</Badge>{" "}
              <span className="font-medium">{g.gap}</span> — {g.detail}
              <div className="text-[10px] text-slate-500 italic mt-0.5">Remediation: {g.remediation}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-23 · FAIR LENDING DISPARATE IMPACT
// ═════════════════════════════════════════════════════════════════════════
export function BankingFairLending() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/fair-lending/disparate-impact").then(r => setData(r.data)).catch(() => toast.error("Fair-lending load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-fair-lending">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Scale className="w-6 h-6 text-rose-500" /> Fair Lending Monitoring</h1>
          <Badge className="bg-rose-50 text-rose-700 border-rose-200">Wave 23</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Applications" value={fmt(data.summary.total_applications)} sub="Lifetime" icon={FileSearch} testid="kpi-fl-total" />
        <Kpi label="Approve rate" value={`${data.summary.overall_approve_pct}%`} sub="Overall" icon={CheckCircle2} testid="kpi-fl-approve" />
        <Kpi label="Manual override" value={`${data.summary.manual_override_pct}%`} sub="Of total apps" tone={data.summary.manual_override_pct > 10 ? "amber" : "emerald"} icon={Hash} testid="kpi-fl-manual" />
        <Kpi label="AAN delivery" value={`${data.summary.aan_delivery_pct}%`} sub="Within 30d" tone={data.summary.aan_delivery_pct >= 95 ? "emerald" : "amber"} icon={Mail} testid="kpi-fl-aan" />
        <Kpi label="State spread" value={`${data.summary.state_spread_pp}pp`} sub="Approval %" tone={data.summary.disparate_impact_concern ? "rose" : "emerald"} icon={Globe} testid="kpi-fl-spread" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-2">Approval rate by state (geographic proxy)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead className="bg-slate-50"><tr className="text-left text-slate-500">
              <th className="px-3 py-2">State</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Approved</th>
              <th className="px-3 py-2 text-right">Denied</th>
              <th className="px-3 py-2 text-right">Approve %</th>
            </tr></thead>
            <tbody>
              {data.by_state.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`fl-state-${i}`}>
                  <td className="px-3 py-2 font-mono">{r.state}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.total}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.approved}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.denied}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.approve_pct}%</td>
                </tr>
              ))}
              {!data.by_state.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No state-level metadata yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4 text-[11.5px]">
        <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Section 1071 (Small Business Loan Data)</div>
        <div className="text-slate-700">Applicable: <strong>{data.section_1071.applicable ? "yes" : "no"}</strong> · Collection implemented: <strong>{data.section_1071.data_collection_implemented ? "yes" : "no"}</strong></div>
        <div className="text-[10.5px] text-slate-600 mt-1 italic">{data.section_1071.remediation}</div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700">{data.ecoa_attestation}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-36 · NACHA ATTESTATION
// ═════════════════════════════════════════════════════════════════════════
export function BankingNacha() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/nacha/attestation").then(r => setData(r.data)).catch(() => toast.error("NACHA load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-nacha">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Banknote className="w-6 h-6 text-sky-500" /> NACHA Originator Attestation</h1>
          <Badge className="bg-sky-50 text-sky-700 border-sky-200">Wave 36</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="ACH txns · 60d" value={fmt(data.ach_volume.total_txns_60d)} sub="Volume" icon={Activity} testid="kpi-nacha-vol" />
        <Kpi label="Returned · 60d" value={fmt(data.ach_volume.returned_60d)} sub="Returns total" tone={data.ach_volume.returned_60d > 0 ? "amber" : "emerald"} icon={XCircle} testid="kpi-nacha-ret" />
        <Kpi label="WSUD · 60d" value={fmt(data.ach_volume.wsud_filed_60d)} sub="Unauthorized" tone={data.ach_volume.wsud_filed_60d > 0 ? "amber" : "emerald"} icon={AlertOctagon} testid="kpi-nacha-wsud" />
        <Kpi label="Admin returns · 60d" value={fmt(data.ach_volume.admin_returns_60d)} sub="R03 + R04" tone="emerald" icon={Hash} testid="kpi-nacha-admin" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-3">Rate-table vs NACHA ceilings</div>
        <div className="space-y-2">
          {data.rates.map((r, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-[11.5px]" data-testid={`nacha-rate-${i}`}>
              <div className="font-medium">{r.metric}</div>
              <div className="flex items-center gap-3">
                <span className="tabular-nums">{r.value}% / {r.ceiling}% ceiling</span>
                <Badge className={TONE_CLS[r.tone]}>{r.tone === "emerald" ? "within" : "over"}</Badge>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-3">Compliance attestations</div>
        <div className="space-y-2">
          {data.attestations.map((a, i) => (
            <div key={i} className="flex items-start justify-between border-b border-slate-100 last:border-0 py-2 text-[11.5px]" data-testid={`nacha-att-${i}`}>
              <div className="max-w-xl">
                <div className="font-medium">{a.item}</div>
                <div className="text-[10.5px] text-slate-500">{a.detail}</div>
              </div>
              <Badge className={TONE_CLS[a.tone]}>{a.attested ? "✓ attested" : "gap"}</Badge>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700 italic">{data.honest_note}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-40 · LIQUIDITY POSTURE
// ═════════════════════════════════════════════════════════════════════════
export function BankingLiquidity() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/liquidity/posture").then(r => setData(r.data)).catch(() => toast.error("Liquidity load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-liquidity">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Sigma className="w-6 h-6 text-emerald-500" /> Liquidity & Settlement</h1>
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Wave 40</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="DDA balance" value={money(data.summary.fbo_balance_usd)} sub="Custody (U.S. Bank)" tone="emerald" icon={Banknote} testid="kpi-fbo" />
        <Kpi label="Pending out" value={money(data.summary.pending_obligations_usd)} sub="T+0 obligations" tone={data.summary.healthy ? "emerald" : "rose"} icon={Activity} testid="kpi-obl" />
        <Kpi label="Coverage ratio" value={data.summary.coverage_ratio == null ? "∞" : `${data.summary.coverage_ratio}x`} sub={data.summary.healthy ? "Healthy ≥1.0" : "BELOW 1.0"} tone={data.summary.tone} icon={CheckCircle2} testid="kpi-cov" />
        <Kpi label="Stress coverage" value={`${data.summary.stress_coverage_ratio}x`} sub="Top-3 customer drawdown" tone={data.summary.stress_healthy ? "emerald" : "amber"} icon={AlertTriangle} testid="kpi-stress" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-2">Per-corridor volume (30d)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead className="bg-slate-50"><tr className="text-left text-slate-500">
              <th className="px-3 py-2">Corridor</th>
              <th className="px-3 py-2 text-right">Txns</th>
              <th className="px-3 py-2 text-right">Volume 30d</th>
            </tr></thead>
            <tbody>
              {data.corridors.map((c, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`liq-corr-${i}`}>
                  <td className="px-3 py-2 font-mono">{c.corridor}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(c.txn_count)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(c.volume_30d_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700">{data.attestation}<div className="mt-1 italic text-[10.5px]">{data.notes}</div></div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-43 · DEFENSE POSTURE
// ═════════════════════════════════════════════════════════════════════════
export function BankingDefense() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/defense/cyber-posture").then(r => setData(r.data)).catch(() => toast.error("Defense load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-defense">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-violet-500" /> DDoS / Bot / WAF Defense</h1>
          <Badge className="bg-violet-50 text-violet-700 border-violet-200">Wave 43</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Active layers" value={`${data.summary.active_layers}/${data.summary.total_layers}`} sub="Defense in depth" tone={data.summary.tone} icon={ShieldCheck} testid="kpi-def-layers" />
        <Kpi label="Rate-limit hits 30d" value={fmt(data.summary.rate_limit_hits_30d)} sub="Mitigated" icon={Activity} testid="kpi-def-rl" />
        <Kpi label="Failed logins 30d" value={fmt(data.summary.failed_logins_30d)} sub="Attack surface" icon={AlertOctagon} testid="kpi-def-fl" />
        <Kpi label="Bad webhook sig 30d" value={fmt(data.summary.webhook_bad_sig_30d)} sub="Bot/replay attempts" icon={Hash} testid="kpi-def-ws" />
        <Kpi label="IPs blocked" value={fmt(data.summary.blocked_ips_active)} sub="Active list" icon={Lock} testid="kpi-def-ips" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-3">Defense-in-depth layers</div>
        <div className="space-y-2">
          {data.defense_layers.map((l, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-[11.5px]" data-testid={`def-layer-${i}`}>
              <div className="max-w-xl">
                <div className="font-medium">{l.layer}</div>
                <div className="text-[10.5px] text-slate-500">{l.detail}</div>
              </div>
              <Badge className={TONE_CLS[l.tone]}>{l.active ? "✓ active" : "gap"}</Badge>
            </div>
          ))}
        </div>
      </div>
      {data.gaps.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
          <div className="font-semibold text-amber-900 mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Honest gaps</div>
          {data.gaps.map((g, i) => (
            <div key={i} className="text-[11.5px] mb-1.5" data-testid={`def-gap-${i}`}>
              <Badge className={TONE_CLS[g.severity === "high" ? "rose" : "amber"]}>{g.severity}</Badge>{" "}
              <span className="font-medium">{g.gap}</span> — {g.detail}
              <div className="text-[10px] text-slate-500 italic mt-0.5">Remediation: {g.remediation}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-47 · EXAMINER MAGIC LINKS
// ═════════════════════════════════════════════════════════════════════════
export function BankingExaminerLinks() {
  const [list, setList] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [examinerName, setExaminerName] = useState("");
  const [agency, setAgency] = useState("");
  const [hours, setHours] = useState("8");
  const [purpose, setPurpose] = useState("");
  const [generated, setGenerated] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/partner/banking/examiner/magic-links").then(r => setList(r.data)).catch(() => toast.error("Magic-link list failed"));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (purpose.length < 8) { toast.error("Purpose ≥8 chars"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/partner/banking/examiner/magic-link", {
        examiner_name: examinerName, examiner_agency: agency,
        expires_in_hours: parseInt(hours, 10), purpose,
      });
      setGenerated(r.data);
      load();
      toast.success("Examiner link issued · share carefully");
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSubmitting(false); }
  };
  const revoke = async (id) => {
    try { await api.post(`/partner/banking/examiner/magic-links/${id}/revoke`); toast.success("Revoked"); load(); }
    catch { toast.error("Revoke failed"); }
  };

  if (!list) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-examiner-links">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><KeyRound className="w-6 h-6 text-amber-500" /> Examiner Magic Links</h1>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">Wave 47</Badge>
          <Button size="sm" onClick={() => { setCreateOpen(true); setGenerated(null); }} data-testid="btn-create-link">+ Issue link</Button>
        </div>
        <p className="text-sm text-slate-500 mt-1">Time-bounded read-only access for OCC/FDIC/state examiners. All page views are audit-logged.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Active links" value={fmt(list.active_count)} sub="Not expired/revoked" tone="amber" icon={KeyRound} testid="kpi-exm-active" />
        <Kpi label="Total issued" value={fmt(list.total)} sub="All time" icon={Hash} testid="kpi-exm-total" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50"><tr className="text-left text-slate-500">
            <th className="px-3 py-2">Examiner</th>
            <th className="px-3 py-2">Agency</th>
            <th className="px-3 py-2">Created</th>
            <th className="px-3 py-2">Expires</th>
            <th className="px-3 py-2">Uses</th>
            <th className="px-3 py-2 text-right">Action</th>
          </tr></thead>
          <tbody>
            {list.rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`exm-row-${i}`}>
                <td className="px-3 py-2 font-medium">{r.examiner_name}</td>
                <td className="px-3 py-2">{r.examiner_agency}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-500">{ts(r.created_at)}</td>
                <td className="px-3 py-2">
                  {r.expired ? <Badge className={TONE_CLS.slate}>expired</Badge> : !r.active ? <Badge className={TONE_CLS.slate}>revoked</Badge> : <Badge className={TONE_CLS.amber}>{r.hours_until_expiry}h left</Badge>}
                </td>
                <td className="px-3 py-2 tabular-nums">{r.uses || 0}</td>
                <td className="px-3 py-2 text-right">
                  {r.active && !r.expired && <Button size="sm" variant="outline" onClick={() => revoke(r.id)} data-testid={`btn-revoke-${i}`}>Revoke</Button>}
                </td>
              </tr>
            ))}
            {!list.rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No examiner links yet. Issue one for your next OCC/FDIC visit.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-create-link">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-500" /> Issue examiner link</DialogTitle>
            <DialogDescription>Read-only · time-bounded · audit-logged. Share carefully.</DialogDescription>
          </DialogHeader>
          {generated ? (
            <div className="space-y-2">
              <div className="text-[11.5px]"><strong>Examiner:</strong> {generated.examiner_name} ({generated.examiner_agency})</div>
              <div className="text-[11.5px]"><strong>Expires:</strong> {ts(generated.expires_at)}</div>
              <div>
                <Label className="text-[11.5px]">URL · copy & share (one-time display)</Label>
                <Input readOnly value={generated.url} className="font-mono text-[10.5px]" onClick={e => e.target.select()} data-testid="generated-url" />
              </div>
              <div className="text-[10.5px] text-slate-500 italic">{generated.note}</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div><Label className="text-[11.5px]">Examiner name</Label><Input value={examinerName} onChange={e => setExaminerName(e.target.value)} placeholder="e.g. John Smith" data-testid="input-exm-name" /></div>
              <div><Label className="text-[11.5px]">Agency</Label><Input value={agency} onChange={e => setAgency(e.target.value)} placeholder="e.g. OCC · FDIC · NYDFS" data-testid="input-exm-agency" /></div>
              <div><Label className="text-[11.5px]">Expires in (1-72 hours)</Label><Input type="number" min="1" max="72" value={hours} onChange={e => setHours(e.target.value)} data-testid="input-exm-hours" /></div>
              <div><Label className="text-[11.5px]">Purpose / scope (≥8 chars · audit-logged)</Label><Textarea value={purpose} onChange={e => setPurpose(e.target.value)} rows={2} placeholder="e.g. OCC Q1-2026 BSA/AML examination · review SAR pipeline + sanctions coverage" data-testid="input-exm-purpose" /></div>
            </div>
          )}
          <DialogFooter>
            {generated
              ? <Button onClick={() => { setCreateOpen(false); setGenerated(null); setExaminerName(""); setAgency(""); setPurpose(""); }}>Done</Button>
              : <>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                  <Button onClick={create} disabled={submitting || purpose.length < 8 || !examinerName || !agency} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-confirm-create">
                    {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Issue link
                  </Button>
                </>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-50 · VENDOR CONCENTRATION
// ═════════════════════════════════════════════════════════════════════════
export function BankingVendorConcentration() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/vendor-concentration").then(r => setData(r.data)).catch(() => toast.error("Vendor load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-vendor">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Plug className="w-6 h-6 text-amber-500" /> Vendor Concentration</h1>
          <Badge className="bg-amber-50 text-amber-700 border-amber-200">Wave 50</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Total vendors" value={fmt(data.summary.total_vendors)} sub="In inventory" icon={Plug} testid="kpi-v-total" />
        <Kpi label="Tier 1" value={fmt(data.summary.tier_1_critical_count)} sub="Critical" tone="rose" icon={AlertTriangle} testid="kpi-v-t1" />
        <Kpi label="Single-point-of-failure" value={fmt(data.summary.single_point_of_failure)} sub="Documented exits" tone="amber" icon={AlertOctagon} testid="kpi-v-spof" />
        <Kpi label="SOC 2 attested" value={`${data.summary.soc2_attested}/${data.summary.total_vendors}`} sub="Vendor attestations" tone="emerald" icon={ShieldCheck} testid="kpi-v-soc" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50"><tr className="text-left text-slate-500">
            <th className="px-3 py-2">Vendor</th>
            <th className="px-3 py-2">Function</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2">SOC 2</th>
            <th className="px-3 py-2">SPOF</th>
            <th className="px-3 py-2">Exit plan</th>
          </tr></thead>
          <tbody>
            {data.vendors.map((v, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`v-row-${i}`}>
                <td className="px-3 py-2 font-medium">{v.vendor}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-600">{v.function}</td>
                <td className="px-3 py-2"><Badge className={TONE_CLS[v.criticality === "tier_1" ? "rose" : v.criticality === "tier_2" ? "amber" : "slate"]}>{v.criticality}</Badge></td>
                <td className="px-3 py-2">{v.soc2 ? "✓" : "—"}</td>
                <td className="px-3 py-2">{v.single_point_of_failure ? <Badge className={TONE_CLS.amber}>yes</Badge> : "—"}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-600">{v.exit_plan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700 italic">{data.honest_note}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// Wave-42 · ONBOARDING FUNNEL
// ═════════════════════════════════════════════════════════════════════════
export function BankingFunnel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/partner/banking/onboarding-funnel?window_days=30").then(r => setData(r.data)).catch(() => toast.error("Funnel load failed")); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  return (
    <div className="space-y-5" data-testid="banking-funnel">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><TrendingUp className="w-6 h-6 text-sky-500" /> Onboarding Funnel</h1>
          <Badge className="bg-sky-50 text-sky-700 border-sky-200">Wave 42</Badge>
        </div>
        <p className="text-sm text-slate-500 mt-1">{data.framework}</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Signups started" value={fmt(data.summary.signups_started)} sub={`${data.window_days}d window`} icon={Users} testid="kpi-fnl-start" />
        <Kpi label="Fully onboarded" value={fmt(data.summary.fully_onboarded)} sub="Account funded" tone="emerald" icon={CheckCircle2} testid="kpi-fnl-end" />
        <Kpi label="Conversion" value={`${data.summary.overall_conversion_pct}%`} sub="Healthy 30-70%" tone={data.summary.tone} icon={Hash} testid="kpi-fnl-pct" />
        <Kpi label="Rejections" value={`${data.rejections.ein_fail_rate_pct + data.rejections.kyc_deny_rate_pct}%`} sub="EIN + KYC fails" tone="amber" icon={XCircle} testid="kpi-fnl-rej" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="font-semibold mb-3">Funnel by step</div>
        <div className="space-y-2">
          {data.funnel.map((s, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 last:border-0 py-2 text-[11.5px]" data-testid={`fnl-step-${s.step}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{i + 1}</Badge>
                <span className="font-medium">{s.label}</span>
              </div>
              <div className="flex items-center gap-3 tabular-nums">
                <span>{fmt(s.count)}</span>
                {s.drop_off != null && s.drop_off > 0 && <span className="text-rose-500 text-[10.5px]">−{fmt(s.drop_off)} drop</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-[11.5px]">
        <div className="font-semibold mb-2">Rejection breakdown</div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="text-slate-500 text-[10px]">EIN failed</div><div className="font-semibold tabular-nums">{fmt(data.rejections.ein_failed)} ({data.rejections.ein_fail_rate_pct}%)</div></div>
          <div><div className="text-slate-500 text-[10px]">KYC denied</div><div className="font-semibold tabular-nums">{fmt(data.rejections.kyc_denied)} ({data.rejections.kyc_deny_rate_pct}%)</div></div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[11.5px] text-slate-700 italic">{data.notes}</div>
    </div>
  );
}
